/**
 * Pricing utilities for GST inclusive/exclusive conversions and tax breakup calculations.
 * 
 * Key concepts:
 * - "base" or "exclusive" price: the taxable amount before GST (stored in DB)
 * - "inclusive" price: the final price including all taxes (MRP-style)
 * - GST can be CGST+SGST (intrastate) or IGST (interstate); IGST = CGST + SGST
 */

/**
 * Round a number to 2 decimal places.
 * @param {number} n 
 * @returns {number}
 */
export function round2(n) {
  if (n === '' || n === null || n === undefined || Number.isNaN(Number(n))) return 0;
  return Math.round(Number(n) * 100) / 100;
}

/**
 * Convert a value to a number, returning 0 for invalid/empty values.
 * @param {*} val 
 * @returns {number}
 */
export function toNum(val) {
  if (val === '' || val === null || val === undefined) return 0;
  const n = Number(val);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Calculate the effective GST rate (IGST or CGST+SGST) plus Cess.
 * @param {{ cgstRate?: number, sgstRate?: number, igstRate?: number, cessRate?: number }} rates 
 * @returns {{ gstRate: number, cessRate: number, totalTaxRate: number }}
 */
export function getTaxRates(rates) {
  const cgst = toNum(rates?.cgstRate);
  const sgst = toNum(rates?.sgstRate);
  const igst = toNum(rates?.igstRate);
  const cess = toNum(rates?.cessRate);
  
  // Use IGST if explicitly set and > 0, otherwise use CGST + SGST
  const gstRate = igst > 0 ? igst : (cgst + sgst);
  const totalTaxRate = gstRate + cess;
  
  return { gstRate, cessRate: cess, totalTaxRate };
}

/**
 * Calculate base (tax-exclusive) price from inclusive price.
 * Formula: base = inclusive / (1 + totalTaxRate/100)
 * @param {number} inclusive - The tax-inclusive price
 * @param {{ cgstRate?: number, sgstRate?: number, igstRate?: number, cessRate?: number }} rates 
 * @returns {number} - The base price (tax-exclusive)
 */
export function baseFromInclusive(inclusive, rates) {
  const { totalTaxRate } = getTaxRates(rates);
  const inclusiveNum = toNum(inclusive);
  if (totalTaxRate <= 0) return round2(inclusiveNum);
  return round2(inclusiveNum / (1 + totalTaxRate / 100));
}

/**
 * Calculate inclusive price from base (tax-exclusive) price.
 * Formula: inclusive = base * (1 + totalTaxRate/100)
 * @param {number} base - The tax-exclusive price
 * @param {{ cgstRate?: number, sgstRate?: number, igstRate?: number, cessRate?: number }} rates 
 * @returns {number} - The inclusive price
 */
export function inclusiveFromBase(base, rates) {
  const { totalTaxRate } = getTaxRates(rates);
  const baseNum = toNum(base);
  return round2(baseNum * (1 + totalTaxRate / 100));
}

/**
 * Calculate a full tax breakup from base price.
 * @param {number} base - The tax-exclusive price
 * @param {{ cgstRate?: number, sgstRate?: number, igstRate?: number, cessRate?: number }} rates 
 * @returns {{ base: number, cgstAmt: number, sgstAmt: number, igstAmt: number, cessAmt: number, gstAmt: number, total: number }}
 */
export function breakupFromBase(base, rates) {
  const baseNum = toNum(base);
  const cgst = toNum(rates?.cgstRate);
  const sgst = toNum(rates?.sgstRate);
  const igst = toNum(rates?.igstRate);
  const cess = toNum(rates?.cessRate);
  
  // Compute individual tax amounts
  const cgstAmt = round2((baseNum * cgst) / 100);
  const sgstAmt = round2((baseNum * sgst) / 100);
  const igstAmt = round2((baseNum * igst) / 100);
  const cessAmt = round2((baseNum * cess) / 100);
  
  // GST total (either IGST or CGST+SGST, not both)
  const gstAmt = igst > 0 ? igstAmt : (cgstAmt + sgstAmt);
  
  // Total inclusive price
  const total = round2(baseNum + gstAmt + cessAmt);
  
  return {
    base: round2(baseNum),
    cgstAmt,
    sgstAmt,
    igstAmt,
    cessAmt,
    gstAmt,
    total
  };
}

/**
 * Calculate a full tax breakup from inclusive price.
 * @param {number} inclusive - The tax-inclusive price
 * @param {{ cgstRate?: number, sgstRate?: number, igstRate?: number, cessRate?: number }} rates 
 * @returns {{ base: number, cgstAmt: number, sgstAmt: number, igstAmt: number, cessAmt: number, gstAmt: number, total: number }}
 */
export function breakupFromInclusive(inclusive, rates) {
  const base = baseFromInclusive(inclusive, rates);
  return breakupFromBase(base, rates);
}

/**
 * Format a number as INR currency.
 * @param {number} val 
 * @returns {string}
 */
export function formatINR(val) {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(toNum(val));
  } catch {
    return String(val);
  }
}
